import { c as _c } from "react/compiler-runtime";
import type { TFunction } from 'shared/translations';
import Autocomplete, { type AutocompleteOptionItem } from '@precis-digital/kurama/Autocomplete';
import { DropdownItem, DropdownList, DropdownListSubCategory } from '@precis-digital/kurama/Dropdown';
import Grid from '@precis-digital/kurama/Grid';
import Input from '@precis-digital/kurama/Input';
import Radio from '@precis-digital/kurama/Radio';
import SelectInput, { SelectItem } from '@precis-digital/kurama/SelectInput';
import Typography from '@precis-digital/kurama/Typography';
import { useTheme } from '@precis-digital/kurama/utils';
import Skeleton from '@precis-digital/kurama/Skeleton';
import { groupBy } from 'ramda';
import { type FilterFormValues, type Option } from 'shared/components/Filter';
import type { FilterTFunction } from './types';
import { type ItemComponentProps } from './FilterFlyout';
import { sortInAlphabeticalOrder } from 'shared/components/Filter/utils';
import FormItem from 'shared/components/FormItem';
import { OPERATOR_OPTIONS, type OperatorType } from 'shared/constants/filterOperators';
import { handleRowValueValidation } from 'shared/utils';
interface ParameterFilterProps extends ItemComponentProps<FilterFormValues> {
  parameterOptions: Option[];
}
export const ParameterFilterItem = t0 => {
  const $ = _c(7);
  const {
    parameterOptions,
    formValues,
    setValue
  } = t0;
  let t1;
  if ($[0] !== formValues || $[1] !== parameterOptions || $[2] !== setValue) {
    const groupedOptionsByCategory = groupBy(_temp, parameterOptions?.filter(_temp2) ?? []);
    const optionsWithoutCategory = parameterOptions?.filter(_temp3) ?? [];
    let t2;
    if ($[4] !== formValues || $[5] !== setValue) {
      t2 = option_2 => <DropdownItem key={option_2.value} onClick={() => {
        setValue("parameter", option_2);
        setValue("operator", null);
      }} control={<Radio checked={formValues.parameter?.value === option_2.value} />}>{option_2.label}</DropdownItem>;
      $[4] = formValues;
      $[5] = setValue;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = <DropdownList>{optionsWithoutCategory.map(t2)}{Object.entries(groupedOptionsByCategory).map(t3 => {
        const [subCategory, options] = t3;
        return <DropdownListSubCategory header={subCategory} key={subCategory}>{options.map(option_3 => <DropdownItem key={option_3.value} onClick={() => {
            setValue("parameter", option_3);
            setValue("operator", null);
          }} control={<Radio checked={formValues.parameter?.value === option_3.value} />}>{option_3.label}</DropdownItem>)}</DropdownListSubCategory>;
      })}</DropdownList>;
    $[0] = formValues;
    $[1] = parameterOptions;
    $[2] = setValue;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
interface OperatorFilterItemProps extends ItemComponentProps<FilterFormValues> {
  t: FilterTFunction;
  parameterOptions?: Option[];
  allowCustomInput?: boolean;
  tCommon?: TFunction;
  unit?: string | null;
  isLoading?: boolean;
}
export const OperatorFilterItem = t0 => {
  const $ = _c(38);
  const {
    t,
    formValues,
    setValue,
    control,
    parameterOptions,
    allowCustomInput: t1,
    tCommon,
    errors,
    unit,
    isLoading: t2
  } = t0;
  const allowCustomInput = t1 === undefined ? false : t1;
  const isLoading = t2 === undefined ? false : t2;
  const theme = useTheme();
  const type = formValues?.operator?.type;
  const operatorRequiresValue = type != null;
  let t3;
  if ($[0] !== formValues.parameter?.value || $[1] !== parameterOptions) {
    t3 = parameterOptions?.find(option => option.value === formValues.parameter?.value)?.operators ?? OPERATOR_OPTIONS;
    $[0] = formValues.parameter?.value;
    $[1] = parameterOptions;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const parsedOperatorOptions = t3;
  let t4;
  if ($[3] !== errors?.data?.message) {
    t4 = errors?.data?.message?.toString();
    $[3] = errors?.data?.message;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const dataErrorMessage = t4;
  let t5;
  if ($[5] !== dataErrorMessage) {
    t5 = dataErrorMessage != null && dataErrorMessage !== "" ? {
      color: "error" as const,
      helperText: dataErrorMessage
    } : {};
    $[5] = dataErrorMessage;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const errorProps = t5;
  let t6;
  if ($[7] !== theme) {
    t6 = theme.spacing(6);
    $[7] = theme;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      required: true
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t) {
    t8 = t("filter.selectOperator");
    $[10] = t;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== formValues.operator?.id || $[13] !== parsedOperatorOptions || $[14] !== setValue) {
    t9 = t10 => {
      const {
        field
      } = t10;
      return <SelectInput fullWidth={true} {...field} value={formValues.operator?.id ?? ""} onChange={event => {
        const conditionOperator = parsedOperatorOptions[event.target.value as string];
        setValue("operator", conditionOperator);
        setValue("data", null);
      }}>{Object.values(parsedOperatorOptions).map(_temp4)}</SelectInput>;
    };
    $[12] = formValues.operator?.id;
    $[13] = parsedOperatorOptions;
    $[14] = setValue;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== control || $[17] !== t8 || $[18] !== t9) {
    t10 = <FormItem rules={t7} isCreateEvent={true} label={t8} name="operator" control={control} render={t9} />;
    $[16] = control;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== allowCustomInput || $[21] !== control || $[22] !== errorProps || $[23] !== formValues.data || $[24] !== formValues.operator?.id || $[25] !== formValues.parameter?.options || $[26] !== isLoading || $[27] !== operatorRequiresValue || $[28] !== setValue || $[29] !== t || $[30] !== tCommon || $[31] !== type || $[32] !== unit) {
    t11 = operatorRequiresValue ? <Grid flexDirection="column"><br />{(type === "string" || type === "number") && <FormItem rules={{
        required: true,
        ...(tCommon != null && {
          validate: value => {
            if (typeof value !== "string") {
              return;
            }
            return handleRowValueValidation({
              operator: formValues.operator?.id as OperatorType,
              t: tCommon
            })(value);
          }
        })
      }} isCreateEvent={true} shouldUnregister={true} label={t("filter.value")} name="data" control={control} render={t12 => {
        const {
          field: field_0
        } = t12;
        const stringValue = typeof formValues.data === "string" ? formValues.data : "";
        return <Input fullWidth={true} type="text" {...field_0} value={stringValue} onChange={event_0 => {
          setValue("data", event_0.target.value);
        }} rightIcon={unit != null ? <Typography variant="body2">{unit}</Typography> : undefined} {...type === "number" && {
          inputProps: {
            inputMode: "decimal" as const
          }
        }} {...errorProps} />;
      }} />}{type === "single_select" && <FormItem rules={{
        required: true
      }} isCreateEvent={true} shouldUnregister={true} label={t("filter.value")} name="data" control={control} render={t13 => {
        const {
          field: field_1
        } = t13;
        if (isLoading) {
          return <Skeleton variant="rectangular" height={40} />;
        }
        return <SelectInput fullWidth={true} {...field_1} onChange={event_1 => {
          setValue("data", {
            value: event_1.target.value as string,
            label: formValues.parameter?.options?.find(option_0 => option_0.value === event_1.target.value)?.label ?? ""
          });
        }} value={(formValues.data as Option)?.value ?? ""}>{formValues.parameter?.options?.sort(sortInAlphabeticalOrder).map(_temp5)}</SelectInput>;
      }} />}{type === "multi_select" && (formValues.data == null || Array.isArray(formValues.data)) && <FormItem rules={{
        required: true
      }} isCreateEvent={true} shouldUnregister={true} label={t("filter.value")} name="data" control={control} render={t14 => {
        const {
          field: field_2
        } = t14;
        if (isLoading) {
          return <Skeleton variant="rectangular" height={40} />;
        }
        return <Autocomplete fullWidth={true} {...field_2} allowCustomInput={allowCustomInput} onChange={(_, value_0) => {
          setValue("data", value_0);
        }} multiple={true} dropdownPlacement="top" value={formValues.data as AutocompleteOptionItem[] ?? []} options={formValues.parameter?.options?.sort(sortInAlphabeticalOrder).map(_temp6) ?? []} />;
      }} />}</Grid> : null;
    $[20] = allowCustomInput;
    $[21] = control;
    $[22] = errorProps;
    $[23] = formValues.data;
    $[24] = formValues.operator?.id;
    $[25] = formValues.parameter?.options;
    $[26] = isLoading;
    $[27] = operatorRequiresValue;
    $[28] = setValue;
    $[29] = t;
    $[30] = tCommon;
    $[31] = type;
    $[32] = unit;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t10 || $[35] !== t11 || $[36] !== t6) {
    t12 = <Grid flexDirection="column" gap={t6}>{t10}{t11}</Grid>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t6;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  return t12;
};
function _temp(option) {
  return option?.subCategory ?? "";
}
function _temp2(option_0) {
  return option_0.subCategory != null;
}
function _temp3(option_1) {
  return option_1.subCategory == null;
}
function _temp4(dimension) {
  return <SelectItem value={dimension.id} key={dimension.id}><Typography variant="body2">{dimension.label}</Typography></SelectItem>;
}
function _temp5(option_1) {
  return <SelectItem key={option_1.value} value={option_1.value}>{option_1.label}</SelectItem>;
}
function _temp6(option_2) {
  return {
    value: option_2.value,
    label: option_2.label
  };
}